import { useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

/**
 * Состояние сети.
 *
 * `navigator.onLine` отвечает только за наличие соединения у устройства и
 * ничего не знает о том, доступен ли наш сервер, поэтому это не единственный
 * источник правды: экраны дополнительно показывают ошибку запроса. Здесь
 * важно другое — в Telegram Mini App пользователь часто уходит в лифт и
 * возвращается, и приложение должно само подтянуть данные, а не висеть
 * пустым до перезапуска.
 */
export function useOnline(): boolean {
  const [online, setOnline] = useState(() =>
    typeof navigator === 'undefined' ? true : navigator.onLine,
  );
  const qc = useQueryClient();

  useEffect(() => {
    const up = () => {
      setOnline(true);
      // Возврат связи — единственный момент, когда переспрашивать сервер
      // безопасно и уместно: экран уже открыт, а данные на нём устарели.
      void qc.refetchQueries({ type: 'active' });
    };
    const down = () => setOnline(false);

    window.addEventListener('online', up);
    window.addEventListener('offline', down);
    return () => {
      window.removeEventListener('online', up);
      window.removeEventListener('offline', down);
    };
  }, [qc]);

  return online;
}

/**
 * Запрос, поставленный на паузу из-за отсутствия сети.
 *
 * react-query в режиме networkMode:'online' не отправляет запрос без связи и
 * оставляет его в состоянии pending + fetchStatus:'paused'. Ошибки не будет
 * никогда, поэтому без этой проверки экран навсегда застревает на скелетоне.
 */
export function isPaused(query: { isPending: boolean; fetchStatus: string }): boolean {
  return query.isPending && query.fetchStatus === 'paused';
}

/** Ошибка запроса, вызванная сетью, а не ответом сервера. */
export function isNetworkError(error: unknown): boolean {
  if (typeof navigator !== 'undefined' && !navigator.onLine) return true;
  const status = (error as { status?: number } | null)?.status;
  return status === 0;
}
